/**
 * CampaignDetailView - Vista de detalle de campaña
 * Muestra steps, estadísticas y permite edición (admin)
 */

import { useState, useEffect } from 'react'
import { campaignsAPI, adminAPI } from '../../services/api'
import Toggle from '../shared/Toggle'
import InstantModeSwitch from './InstantModeSwitch'
import KeywordTriggerControl from './KeywordTriggerControl'
import StepEditor from './StepEditor'
import ContactSelector from './ContactSelector'

// Caché local de URLs de imágenes (persiste mientras el componente padre esté montado)
const imageUrlCache = new Map()

/**
 * Componente de miniatura de imagen de Wassenger
 * Carga la URL de la imagen de forma lazy y la muestra en miniatura
 */
function ImageThumbnail({ fileId, className = '' }) {
  const [imageUrl, setImageUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!fileId) {
      setLoading(false)
      return
    }

    // Revisar caché local primero
    if (imageUrlCache.has(fileId)) {
      setImageUrl(imageUrlCache.get(fileId))
      setLoading(false)
      return
    }

    // Cargar desde API
    const loadImage = async () => {
      try {
        const response = await campaignsAPI.getFilePreview(fileId)
        // Priorizar preview base64 (más eficiente), luego URL completa
        const imageSource = response?.file?.preview || response?.file?.url
        if (imageSource) {
          imageUrlCache.set(fileId, imageSource)
          setImageUrl(imageSource)
        } else {
          setError(true)
        }
      } catch (err) {
        console.warn('Error cargando preview:', err)
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    loadImage()
  }, [fileId])

  if (!fileId) return null

  if (loading) {
    return (
      <div className={`bg-gray-100 rounded flex items-center justify-center ${className}`}>
        <div className="animate-pulse bg-gray-200 w-full h-full rounded"></div>
      </div>
    )
  }

  if (error || !imageUrl) {
    return (
      <div className={`bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs ${className}`}>
        <span>Sin preview</span>
      </div>
    )
  }

  return (
    <img
      src={imageUrl}
      alt="Preview"
      className={`object-cover rounded ${className}`}
      onError={() => setError(true)}
    />
  )
}

export default function CampaignDetailView({ campaign, isAdmin, onUpdate, onDelete }) {
  const [activeTab, setActiveTab] = useState('steps') // 'steps' | 'add' | 'enrollments' | 'responded' | 'stats'
  const [stats, setStats] = useState(null)
  const [enrollments, setEnrollments] = useState([])
  const [loadingStats, setLoadingStats] = useState(false)
  const [loadingEnrollments, setLoadingEnrollments] = useState(false)
  const [editingStep, setEditingStep] = useState(null)
  const [showStepModal, setShowStepModal] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState(false)
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    status: 'draft',
    category_id: ''
  })
  // Estado para selección múltiple de enrollments
  const [selectedEnrollments, setSelectedEnrollments] = useState(new Set())
  const [removingBulk, setRemovingBulk] = useState(false)
  const [categories, setCategories] = useState([])

  // Cargar datos cuando cambia la campaña
  useEffect(() => {
    if (campaign) {
      setCampaignForm({
        name: campaign.name || '',
        description: campaign.description || '',
        status: campaign.status || 'draft',
        category_id: campaign.category_id || ''
      })
    }
  }, [campaign])

  useEffect(() => {
    if (isAdmin) adminAPI.getCampaignCategories().then(d => setCategories(d.categories || [])).catch(() => {})
  }, [isAdmin])

  // Cargar estadísticas
  useEffect(() => {
    if (activeTab === 'stats' && campaign?.id) {
      loadStats()
    }
  }, [activeTab, campaign?.id])

  // Cargar enrollments
  useEffect(() => {
    if ((activeTab === 'enrollments' || activeTab === 'responded') && campaign?.id) {
      loadEnrollments()
    }
  }, [activeTab, campaign?.id])

  const loadStats = async () => {
    try {
      setLoadingStats(true)
      const data = await campaignsAPI.getCampaignStats(campaign.id)
      setStats(data.stats)
    } catch (err) {
      console.error('Error cargando estadísticas:', err)
    } finally {
      setLoadingStats(false)
    }
  }

  const loadEnrollments = async () => {
    try {
      setLoadingEnrollments(true)
      const data = await campaignsAPI.getEnrollments(campaign.id)
      setEnrollments(data.enrollments || [])
    } catch (err) {
      console.error('Error cargando inscripciones:', err)
    } finally {
      setLoadingEnrollments(false)
    }
  }

  const handleUpdateCampaign = async () => {
    try {
      const res = await campaignsAPI.updateCampaign(campaign.id, campaignForm)
      setEditingCampaign(false)
      // Pasar la campaña actualizada para refresco optimista inmediato de la tarjeta izquierda
      onUpdate(res?.campaign || { id: campaign.id, ...campaignForm })
    } catch (err) {
      console.error('Error actualizando campaña:', err)
      alert('Error al actualizar campaña')
    }
  }

  // Toggle visual: checked=true → status='active', checked=false → status='archived'.
  // 'draft' se trata como apagado en la UI (toggle muestra "Inactiva").
  // Las cohortes en ejecución siguen corriendo aunque la campaña se desactive.
  const handleToggleStatus = async (nextChecked) => {
    const newStatus = nextChecked ? 'active' : 'archived'
    try {
      const res = await campaignsAPI.updateCampaign(campaign.id, { status: newStatus })
      onUpdate(res?.campaign || { id: campaign.id, status: newStatus })
    } catch (err) {
      console.error('Error cambiando estado:', err)
      alert('Error al cambiar estado de la campaña')
    }
  }

  // Modo instantáneo (confirmado por el doble-click del InstantModeSwitch)
  const [instantBusy, setInstantBusy] = useState(false)
  const handleInstantToggle = async (next) => {
    setInstantBusy(true)
    try {
      const res = await campaignsAPI.setInstantMode(campaign.id, next)
      onUpdate(res?.campaign || { id: campaign.id, instant_mode: next })
    } catch (err) {
      console.error('Error cambiando modo instantáneo:', err)
      alert('Error al cambiar el modo instantáneo')
    } finally {
      setInstantBusy(false)
    }
  }

  // Trigger por keyword en la Nota Express (admin)
  const [kwBusy, setKwBusy] = useState(false)
  const handleKeywordSave = async (cfg) => {
    setKwBusy(true)
    try {
      const res = await campaignsAPI.setKeywordTrigger(campaign.id, cfg)
      onUpdate(res?.campaign || { id: campaign.id, trigger_enabled: cfg.enabled, trigger_keyword: cfg.keyword, trigger_match_mode: cfg.matchMode })
    } catch (err) {
      console.error('Error guardando trigger keyword:', err)
      alert('Error al guardar el disparador')
    } finally {
      setKwBusy(false)
    }
  }

  const [duplicating, setDuplicating] = useState(false)

  const handleDuplicate = async () => {
    if (duplicating) return
    setDuplicating(true)
    try {
      const result = await campaignsAPI.duplicateCampaign(campaign.id)
      const copyName = result?.campaign?.name || 'la copia'
      alert(`Campaña duplicada como "${copyName}"`)
      onUpdate()
    } catch (err) {
      console.error('Error duplicando campaña:', err)
      alert('Error al duplicar la campaña')
    } finally {
      setDuplicating(false)
    }
  }

  const handleSaveStep = async (stepData, options = {}) => {
    if (editingStep?.id) {
      await campaignsAPI.updateStep(campaign.id, editingStep.id, stepData)
    } else {
      await campaignsAPI.createStep(campaign.id, stepData, options)
    }
    setShowStepModal(false)
    setEditingStep(null)
    onUpdate()
  }

  const handleDeleteStep = async (stepId) => {
    if (!confirm('¿Eliminar este paso?')) return
    try {
      await campaignsAPI.deleteStep(campaign.id, stepId)
      onUpdate()
    } catch (err) {
      console.error('Error eliminando step:', err)
      alert(err.response?.data?.error || 'No se pudo eliminar el paso')
    }
  }

  const handleExitEnrollment = async (contactPhone) => {
    if (!confirm(`¿Sacar a ${contactPhone} de esta campaña?`)) return
    try {
      await campaignsAPI.exitContact(campaign.id, contactPhone, 'manual_admin')
      loadEnrollments()
    } catch (err) {
      console.error('Error sacando contacto:', err)
    }
  }

  // Funciones para selección múltiple de enrollments
  const toggleEnrollmentSelect = (id) => {
    setSelectedEnrollments(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const toggleSelectAllEnrollments = () => {
    const activeEnrollments = enrollments.filter(e => e.status === 'active')
    if (selectedEnrollments.size === activeEnrollments.length) {
      setSelectedEnrollments(new Set())
    } else {
      setSelectedEnrollments(new Set(activeEnrollments.map(e => e.contacto_id)))
    }
  }

  const handleExitBulk = async () => {
    if (selectedEnrollments.size === 0) return
    if (!confirm(`¿Sacar ${selectedEnrollments.size} contactos de esta campaña?`)) return

    try {
      setRemovingBulk(true)
      const result = await campaignsAPI.exitContactsBulk(
        campaign.id,
        Array.from(selectedEnrollments),
        'manual_admin'
      )
      alert(`${result.removed} contactos removidos`)
      setSelectedEnrollments(new Set())
      loadEnrollments()
      onUpdate()
    } catch (err) {
      console.error('Error removiendo contactos:', err)
      alert('Error al remover contactos')
    } finally {
      setRemovingBulk(false)
    }
  }

  const getStatusBadge = (status) => {
    const config = {
      draft: { bg: 'bg-gray-100 dark:bg-slate-700/50', text: 'text-gray-600 dark:text-gray-300', border: 'border-gray-200 dark:border-slate-600', dot: 'bg-gray-400 dark:bg-gray-500', label: 'Borrador' },
      active: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-800', dot: 'bg-emerald-500', label: 'Activa' },
      archived: { bg: 'bg-slate-100 dark:bg-slate-700/50', text: 'text-slate-600 dark:text-slate-300', border: 'border-slate-300 dark:border-slate-600', dot: 'bg-slate-400 dark:bg-slate-500', label: 'Archivada' }
    }
    return config[status] || config.draft
  }

  const getEnrollmentStatusBadge = (status) => {
    const config = {
      active: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-800', dot: 'bg-emerald-500', label: 'Activo' },
      completed: { bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-800', dot: 'bg-blue-500', label: 'Completado' },
      exited: { bg: 'bg-gray-100 dark:bg-slate-700/50', text: 'text-gray-600 dark:text-gray-300', border: 'border-gray-200 dark:border-slate-600', dot: 'bg-gray-400 dark:bg-gray-500', label: 'Salio' }
    }
    const c = config[status] || config.active
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text} border ${c.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
        {c.label}
      </span>
    )
  }

  if (!campaign) return null

  const steps = campaign.steps || []

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header - Estilo Koomo */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="p-5">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              {editingCampaign ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 text-lg font-medium"
                    placeholder="Nombre de la campana"
                  />
                  <textarea
                    value={campaignForm.description}
                    onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                    placeholder="Descripcion (opcional)"
                    rows={2}
                  />
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Categoría</label>
                    {categories.length > 0 ? (
                      <select
                        value={campaignForm.category_id || ''}
                        onChange={(e) => setCampaignForm({ ...campaignForm, category_id: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Sin categoría</option>
                        {categories.filter(c => c.is_active || c.id === campaignForm.category_id).map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-xs text-gray-400 px-1">No hay categorías aún. Créalas en el panel de Categorías (menú lateral 🏷️).</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdateCampaign}
                      className="px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl text-sm hover:from-primary-700 hover:to-primary-800 font-medium shadow-sm"
                    >
                      Guardar
                    </button>
                    <button
                      onClick={() => setEditingCampaign(false)}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl text-sm font-medium"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold text-gray-900">{campaign.name}</h2>
                    {(() => {
                      const c = getStatusBadge(campaign.status)
                      return (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text} border ${c.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
                          {c.label}
                        </span>
                      )
                    })()}
                  </div>
                  {campaign.description && (
                    <p className="text-sm text-gray-500 mt-1.5">{campaign.description}</p>
                  )}
                </>
              )}
            </div>

            {isAdmin && !editingCampaign && (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingCampaign(true)}
                  className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-xl transition"
                  title="Editar"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <KeywordTriggerControl
                      campaign={campaign}
                      busy={kwBusy}
                      onSave={handleKeywordSave}
                    />
                  )}
                  <InstantModeSwitch
                    enabled={campaign.instant_mode === true}
                    busy={instantBusy}
                    onToggle={handleInstantToggle}
                  />
                </div>
                <Toggle
                  checked={campaign.status === 'active'}
                  onChange={handleToggleStatus}
                  labelOn="Activa"
                  labelOff="Inactiva"
                  title={campaign.status === 'active'
                    ? 'Desactivar campaña (oculta del modal de cohortes)'
                    : 'Activar campaña (queda lista para usar en cohortes)'}
                />
                <button
                  onClick={handleDuplicate}
                  disabled={duplicating}
                  className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Duplicar campaña (crea una copia en borrador, sin cohortes)"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={onDelete}
                  className="w-9 h-9 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-xl transition"
                  title="Eliminar"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs - Estilo Koomo con pills */}
        <div className="px-5 pb-0">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
            {[
              { id: 'steps', label: 'Pasos', count: steps.length },
              { id: 'add', label: 'Agregar', count: null, adminOnly: true },
              { id: 'enrollments', label: 'Inscritos', count: enrollments.length || null },
              { id: 'responded', label: 'Respondieron', count: enrollments.filter(e => e.exit_reason === 'replied').length || null },
              { id: 'stats', label: 'Estadisticas', count: null }
            ].filter(tab => !tab.adminOnly || isAdmin).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.count !== null && tab.count > 0 && (
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="h-4"></div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5">
        {/* Tab: Steps - Estilo Koomo */}
        {activeTab === 'steps' && (
          <div>
            {isAdmin && (
              <button
                onClick={() => {
                  setEditingStep(null)
                  setShowStepModal(true)
                }}
                className="mb-5 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition text-sm font-medium shadow-md hover:shadow-lg inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Agregar Paso
              </button>
            )}

            {steps.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📋</span>
                </div>
                <p className="text-gray-600 font-medium">No hay pasos configurados</p>
                {isAdmin && (
                  <p className="text-sm text-gray-400 mt-1">Agrega pasos para definir la secuencia de mensajes</p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                {steps.sort((a, b) => a.step_number - b.step_number).map((step, idx) => (
                  <div
                    key={step.id}
                    className={`bg-white rounded-xl shadow-sm border transition-all hover:shadow-md ${
                      step.is_active !== false
                        ? 'border-gray-100'
                        : 'border-gray-200 opacity-60'
                    }`}
                  >
                    <div className="p-5">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-lg text-sm flex items-center justify-center font-bold shadow-sm">
                              {step.step_number}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">
                                Dia {step.days_after_enrollment}
                              </span>
                              <span className="text-gray-300">•</span>
                              <span className="text-sm text-gray-500">
                                {String(step.send_hour ?? 10).padStart(2,'0')}:{String(step.send_minute ?? 0).padStart(2,'0')} hrs
                              </span>
                              {step.is_active === false && (
                                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full border border-gray-200">Inactivo</span>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                            {step.message_text || '(Sin mensaje)'}
                          </p>
                          {(step.wassenger_file_id || step.image_url) && (
                            <div className="mt-4 flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                              {/* Miniatura de imagen */}
                              {step.wassenger_file_id ? (
                                <ImageThumbnail
                                  fileId={step.wassenger_file_id}
                                  className="w-16 h-16 flex-shrink-0 rounded-lg"
                                />
                              ) : step.image_url ? (
                                <img
                                  src={step.image_url}
                                  alt="Preview"
                                  className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              ) : null}
                              {/* ID o URL */}
                              <div className="text-xs text-gray-500">
                                <span className="flex items-center gap-2">
                                  <span className="text-gray-400">Imagen adjunta</span>
                                  {step.wassenger_file_id ? (
                                    <span className="font-mono bg-white px-2 py-1 rounded-lg border border-gray-200">
                                      {step.wassenger_file_id.substring(0, 12)}...
                                    </span>
                                  ) : (
                                    <a href={step.image_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline truncate max-w-[200px]">
                                      URL externa
                                    </a>
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1 ml-4">
                            <button
                              onClick={() => {
                                setEditingStep(step)
                                setShowStepModal(true)
                              }}
                              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition"
                              title="Editar"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteStep(step.id)}
                              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Eliminar"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab: Add Contacts */}
        {activeTab === 'add' && isAdmin && (
          <div className="h-full -m-4">
            <ContactSelector
              campaignId={campaign.id}
              onEnrollComplete={() => {
                // Recargar enrollments si estamos viendo ese tab después
                loadEnrollments()
                onUpdate()
              }}
            />
          </div>
        )}

        {/* Tab: Enrollments - Estilo Koomo */}
        {activeTab === 'enrollments' && (
          <div>
            {/* Barra de acciones bulk */}
            {isAdmin && enrollments.filter(e => e.status === 'active').length > 0 && (
              <div className="mb-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={selectedEnrollments.size === enrollments.filter(e => e.status === 'active').length && selectedEnrollments.size > 0}
                      onChange={toggleSelectAllEnrollments}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    Seleccionar activos
                  </label>
                  <span className="text-sm text-gray-500">
                    {selectedEnrollments.size > 0 ? `${selectedEnrollments.size} seleccionados` : `${enrollments.length} inscritos`}
                  </span>
                </div>
                {selectedEnrollments.size > 0 && (
                  <button
                    onClick={handleExitBulk}
                    disabled={removingBulk}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 transition text-sm font-medium shadow-sm"
                  >
                    {removingBulk ? 'Removiendo...' : `Remover (${selectedEnrollments.size})`}
                  </button>
                )}
              </div>
            )}

            {loadingEnrollments ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : enrollments.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">👥</span>
                </div>
                <p className="text-gray-600 font-medium">No hay contactos inscritos</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        {isAdmin && (
                          <th className="w-12 px-3 py-3">
                            <input
                              type="checkbox"
                              checked={selectedEnrollments.size === enrollments.filter(e => e.status === 'active').length && selectedEnrollments.size > 0}
                              onChange={toggleSelectAllEnrollments}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                          </th>
                        )}
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contacto</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Agente</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Progreso</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ultimo envio</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Proximo envio</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Inscrito</th>
                        {isAdmin && <th className="px-3 py-3 w-20"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {enrollments.map((enrollment) => (
                        <tr
                          key={enrollment.id}
                          className={`hover:bg-gray-50 transition ${selectedEnrollments.has(enrollment.contacto_id) ? 'bg-primary-50' : ''}`}
                        >
                          {isAdmin && (
                            <td className="px-3 py-4">
                              {enrollment.status === 'active' && (
                                <input
                                  type="checkbox"
                                  checked={selectedEnrollments.has(enrollment.contacto_id)}
                                  onChange={() => toggleEnrollmentSelect(enrollment.contacto_id)}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                />
                              )}
                            </td>
                          )}
                          <td className="px-3 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{enrollment.contact_name || 'Sin nombre'}</p>
                              <p className="text-xs text-gray-400">{enrollment.contact_phone}</p>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-600">
                            {enrollment.assigned_agent || '-'}
                          </td>
                          <td className="px-3 py-4">
                            {getEnrollmentStatusBadge(enrollment.status)}
                          </td>
                          <td className="px-3 py-4">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-gray-100 rounded-full max-w-[60px]">
                                <div
                                  className="h-2 bg-primary-500 rounded-full transition-all"
                                  style={{ width: `${((enrollment.sent_steps_count || enrollment.current_step || 0) / steps.length) * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600">
                                <span className="font-medium">{enrollment.sent_steps_count || enrollment.current_step || 0}</span>
                                <span className="text-gray-400">/{steps.length}</span>
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500">
                            {(enrollment.last_step_sent_at || enrollment.last_sent_at)
                              ? new Date(enrollment.last_step_sent_at || enrollment.last_sent_at).toLocaleString('es-CL', {
                                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
                                })
                              : '-'}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-600">
                            {enrollment.next_send_at ? (
                              new Date(enrollment.next_send_at).toLocaleString('es-CL', {
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
                              })
                            ) : (enrollment.status === 'active' && (enrollment.current_step ?? 0) === 0 && enrollment.cohort_start_at) ? (
                              // Aún no recibe el paso 1: el primer envío lo rige el start_at de
                              // la cohorte (misma fuente que usa el motor de envío). Se muestra
                              // en gris como "programado" para no confundir con un dato faltante.
                              <span className="text-gray-400 italic" title="Primer envio programado segun la fecha de inicio de la cohorte">
                                {new Date(enrollment.cohort_start_at).toLocaleString('es-CL', {
                                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
                                })}
                                <span className="ml-1 text-[10px] not-italic">(programado)</span>
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500">
                            {new Date(enrollment.enrolled_at).toLocaleDateString()}
                          </td>
                          {isAdmin && (
                            <td className="px-3 py-4 text-right">
                              {enrollment.status === 'active' && (
                                <button
                                  onClick={() => handleExitEnrollment(enrollment.contact_phone)}
                                  className="text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1 hover:bg-red-50 rounded-lg transition"
                                >
                                  Sacar
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab: Responded - Estilo Koomo */}
        {activeTab === 'responded' && (
          <div>
            {loadingEnrollments ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : (() => {
              const respondedContacts = enrollments.filter(e => e.exit_reason === 'replied')
              return respondedContacts.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">💬</span>
                  </div>
                  <p className="text-gray-600 font-medium">Ningun contacto ha respondido aun</p>
                  <p className="text-sm text-gray-400 mt-1">Los contactos que respondan a mensajes de la campana apareceran aqui</p>
                </div>
              ) : (
                <div>
                  <div className="mb-4 p-4 bg-gradient-to-r from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-xl flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-200 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">💬</span>
                    </div>
                    <p className="text-sm text-emerald-700">
                      <span className="font-bold">{respondedContacts.length}</span> contacto(s) respondieron y salieron de la campana automaticamente
                    </p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contacto</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Agente</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Paso al responder</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fecha inscripcion</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fecha respuesta</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {respondedContacts.map((enrollment) => (
                            <tr key={enrollment.id} className="hover:bg-gray-50 transition">
                              <td className="px-3 py-4">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{enrollment.contact_name || 'Sin nombre'}</p>
                                  <p className="text-xs text-gray-400">{enrollment.contact_phone}</p>
                                </div>
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-600">
                                {enrollment.assigned_agent || '-'}
                              </td>
                              <td className="px-3 py-4">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                  Paso {enrollment.current_step || 1}
                                </span>
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500">
                                {new Date(enrollment.enrolled_at).toLocaleDateString('es-CL', {
                                  day: '2-digit', month: '2-digit', year: 'numeric'
                                })}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500">
                                {enrollment.exited_at
                                  ? new Date(enrollment.exited_at).toLocaleDateString('es-CL', {
                                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                    })
                                  : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )
            })()}
          </div>
        )}

        {/* Tab: Stats - Estilo Koomo */}
        {activeTab === 'stats' && (
          <div>
            {loadingStats ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : stats ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                      <span className="text-lg">👥</span>
                    </div>
                    <span className="text-sm text-gray-500 font-medium">Total inscritos</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_enrolled || 0}</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl shadow-sm border border-emerald-200 p-5 hover:shadow-md transition">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-200 rounded-xl flex items-center justify-center">
                      <span className="text-lg">✓</span>
                    </div>
                    <span className="text-sm text-emerald-700 font-medium">Activos</span>
                  </div>
                  <p className="text-3xl font-bold text-emerald-700">{stats.active || 0}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl shadow-sm border border-blue-200 p-5 hover:shadow-md transition">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-200 rounded-xl flex items-center justify-center">
                      <span className="text-lg">🎯</span>
                    </div>
                    <span className="text-sm text-blue-700 font-medium">Completados</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-700">{stats.completed || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                      <span className="text-lg">↩</span>
                    </div>
                    <span className="text-sm text-gray-500 font-medium">Salieron</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-700">{stats.exited || 0}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📊</span>
                </div>
                <p className="text-gray-600 font-medium">No hay estadisticas disponibles</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de edición de step */}
      {showStepModal && (
        <StepEditor
          step={editingStep}
          maxStepNumber={steps.length ? Math.max(...steps.map(s => s.step_number)) + 1 : 1}
          onSave={handleSaveStep}
          onClose={() => {
            setShowStepModal(false)
            setEditingStep(null)
          }}
        />
      )}
    </div>
  )
}
